import {useState, useCallback, useEffect, useMemo} from 'react';

import {useStores} from '../../../Hooks';
import {projectPrivacyLevelEnum, accessItemTypeEnum} from '../../../serverClientShared/sharedEnums';
import useAccessState from '../SettingsShared/useAccessState';
import {fetchItemAccessSources} from '../SettingsShared/privacyHelpers';
import useProjectDirtyCheck from './useProjectDirtyCheck';
import {
    cloneArr,
    initialOwners,
    deriveLevel,
    applyLevelToAccess,
    buildSavePayload,
    projectAccessItemRef,
} from './projectSettingsHelpers';

const useProjectSettings = () => {
    const {projectStore, modalStore, generalStore} = useStores();
    const access = useAccessState();

    const [project, setProject] = useState(null);
    const [originalSnapshot, setOriginalSnapshot] = useState(null);
    const [editedName, setEditedName] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [editedTags, setEditedTags] = useState([]);
    const [editedCoverImage, setEditedCoverImage] = useState('');
    const [editedCoverImageThumb, setEditedCoverImageThumb] = useState('');
    const [editedDefault3DModel, setEditedDefault3DModel] = useState(null);
    const [editedIsRolesPermittedByDefault, setEditedIsRolesPermittedByDefault] = useState(false);
    const [editedProjectOwners, setEditedProjectOwners] = useState([]);
    const [editedPrivacyLevel, setEditedPrivacyLevel] = useState(projectPrivacyLevelEnum.PRIVATE);
    const [originalPrivacyLevel, setOriginalPrivacyLevel] = useState(projectPrivacyLevelEnum.PRIVATE);
    const [loadedAccessId, setLoadedAccessId] = useState(null);

    const initFromProject = useCallback((p) => {
        if (!p) return;
        setProject(p);
        setOriginalSnapshot(p);
        setEditedName(p.ProjectName || '');
        setEditedDescription(p.ProjectDescription || '');
        setEditedTags(cloneArr(p.Tags));
        setEditedCoverImage(p.ProjectCoverImage || '');
        setEditedCoverImageThumb(p.ProjectCoverImageThumb || '');
        setEditedDefault3DModel(p.Default3DModel || null);
        setEditedIsRolesPermittedByDefault(!!p.IsRolesPermittedByDefault);
        setEditedProjectOwners(initialOwners(p));
    }, []);

    const itemId = project?.ProjectIdentifier;
    const isAccessLoaded = !!itemId && loadedAccessId === itemId;

    useEffect(() => {
        if (!itemId || loadedAccessId === itemId) return;
        let cancelled = false;
        const loadAccess = async () => {
            if (!generalStore.userIsWorkspaceAdminOrOwner()) {
                if (!cancelled) setLoadedAccessId(itemId);
                return;
            }
            try {
                const result = modalStore.modalData?.prefetchedAccess
                    || await fetchItemAccessSources(generalStore.workspace, {itemId, nestedRoute: ''});
                if (!cancelled) access.initAccessState(result);
            } catch (error) {
                console.error('Failed to fetch project access:', error);
            } finally {
                if (!cancelled) setLoadedAccessId(itemId);
            }
        };
        loadAccess();
        return () => { cancelled = true; };
    }, [itemId, loadedAccessId, generalStore.workspace]);

    useEffect(() => {
        if (!project || !access.originalAccessSources) return;
        const level = deriveLevel(project, access.originalAccessSources);
        setOriginalPrivacyLevel(level);
        setEditedPrivacyLevel(level);
    }, [project, access.originalAccessSources]);

    const updatePrivacyLevel = useCallback((level) => {
        setEditedPrivacyLevel(level);
        applyLevelToAccess(level, access);
    }, [access]);

    const updateCoverImage = useCallback((imageId, thumbId) => {
        setEditedCoverImage(imageId || '');
        setEditedCoverImageThumb(thumbId || '');
    }, []);

    const edited = useMemo(() => ({
        name: editedName, description: editedDescription, tags: editedTags,
        coverImage: editedCoverImage, coverImageThumb: editedCoverImageThumb,
        default3DModel: editedDefault3DModel,
        isRolesPermittedByDefault: editedIsRolesPermittedByDefault,
        projectOwners: editedProjectOwners,
        privacyLevel: editedPrivacyLevel,
    }), [editedName, editedDescription, editedTags, editedCoverImage, editedCoverImageThumb, editedDefault3DModel, editedIsRolesPermittedByDefault, editedProjectOwners, editedPrivacyLevel]);

    const isMirrored = !!project?.ReferencedToAnotherProject?.Workspace;
    const isFieldsDirty = useProjectDirtyCheck(originalSnapshot, originalPrivacyLevel, edited);
    const isDirty = isFieldsDirty || access.isAccessDirty;

    const executeSave = useCallback(async () => {
        generalStore.isLoaderOpen = true;
        try {
            const accessRef = projectAccessItemRef(project.ProjectIdentifier);
            const ops = [access.saveAccessChanges(generalStore.workspace, accessRef)];
            if (!isMirrored) {
                ops.push(projectStore.updateProject(buildSavePayload(project, edited)));
            }
            await Promise.all(ops);
            await projectStore.refreshProjectDetails(true);
            setOriginalSnapshot(projectStore.currentProject || project);
            setOriginalPrivacyLevel(editedPrivacyLevel);
        } catch (e) {
            console.error('Failed to save project settings:', e);
            modalStore.showErrorMessage(generalStore.translate('somethingWentWrong') || 'Failed to save.');
        }
        generalStore.isLoaderOpen = false;
    }, [project, edited, editedPrivacyLevel, isMirrored, generalStore, projectStore, modalStore, access]);

    const save = useCallback(async () => {
        if (!project || !isDirty) return;
        if (!editedProjectOwners.length) {
            modalStore.showErrorMessage(generalStore.translate('projectMustHaveOwner') || 'Project must have at least one owner');
            return;
        }
        await executeSave();
    }, [project, isDirty, editedProjectOwners, modalStore, generalStore, executeSave]);

    const discard = useCallback(() => {
        if (originalSnapshot) initFromProject(originalSnapshot);
        setEditedPrivacyLevel(originalPrivacyLevel);
        access.discardAccess();
    }, [originalSnapshot, initFromProject, originalPrivacyLevel, access]);

    return {
        project, initFromProject, isMirrored, isAccessLoaded,
        itemId,
        itemType: accessItemTypeEnum.PROJECT,
        nestedRoute: '',
        editedName, setEditedName,
        editedDescription, setEditedDescription,
        editedTags, setEditedTags,
        editedCoverImage, editedCoverImageThumb, updateCoverImage,
        editedDefault3DModel, setEditedDefault3DModel,
        editedIsRolesPermittedByDefault, setEditedIsRolesPermittedByDefault,
        editedProjectOwners, setEditedProjectOwners,
        editedPrivacyLevel, updatePrivacyLevel,
        isDirty, save, discard,
        ...access,
    };
};

export default useProjectSettings;
